// Panel Service - Bilendi/respondi Integration
// Handles Panel-ID capture and redirects

// ============================================================================
// CONFIGURATION - Projekt-spezifische Redirect-URLs (Projekt 155033)
// ============================================================================

const REDIRECT_URLS = {
  complete: 'https://survey.maximiles.com/complete?p=155033_8dbe24e4&m=',
  screenout: 'https://survey.maximiles.com/screenout?p=155033_0749f18f&m=',
  quotasfull: 'https://survey.maximiles.com/quotasfull?p=155033_70d867ad&m=',
  quality: 'https://survey.maximiles.com/quality?p=155033&m=',
  speeder: 'https://survey.maximiles.com/speeder?p=155033&m=',
  duplicate: 'https://survey.maximiles.com/duplicate?p=155033&m=',
  geoip: 'https://survey.maximiles.com/geoip?p=155033&m='
};

// LocalStorage Key für Panel-ID
const PANEL_ID_KEY = 'panel_participant_id';

// ============================================================================
// PANEL-ID HANDLING
// ============================================================================

/**
 * Erfasst Panel-ID aus URL-Parameter 'm'
 * Akzeptiert IDs jeder Länge
 * 
 * Beispiel: https://fairment-fragebogen.de?m=abc123
 */
export function capturePanelId() {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const panelId = urlParams.get('m'); // Bilendi nutzt Parameter 'm'
    
    
    if (!panelId) {
      console.log('Kein Panel-Parameter gefunden - direkter Zugriff oder organischer Traffic');
      return null;
    }

    const trimmedId = panelId.trim();
    return trimmedId;
  } catch (error) {
    console.error('Fehler beim Erfassen der Panel-ID:', error);
    return null;
  }
}

/**
 * Validiert Panel-ID Format
 * Akzeptiert jede nicht-leere ID (KEINE Längenbeschränkung)
 */
export function validatePanelId(id) {
  if (!id || typeof id !== 'string') {
    console.warn('Panel-ID Validierung fehlgeschlagen: ID ist nicht vorhanden oder kein String');
    return false;
  }

  // ID muss nur nicht-leer sein (keine Längenbeschränkung mehr!)
  const trimmedId = id.trim();
  
  if (trimmedId.length === 0) {
    console.warn('Panel-ID Validierung fehlgeschlagen: ID ist leer');
    return false;
  }

  console.log('✅ Panel-ID validiert:', trimmedId.substring(0, Math.min(8, trimmedId.length)) + '...', `(Länge: ${trimmedId.length} Zeichen)`);
  return true;
}

/**
 * Speichert Panel-ID in localStorage für Session-Persistenz
 */
export function storePanelId(id) {
  try {
    if (id && validatePanelId(id)) {
      localStorage.setItem(PANEL_ID_KEY, id);
      console.log('Panel-ID gespeichert');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Fehler beim Speichern der Panel-ID:', error);
    return false;
  }
}

/**
 * Holt gespeicherte Panel-ID aus localStorage
 */
export function getPanelId() {
  try {
    const id = localStorage.getItem(PANEL_ID_KEY);
    return id && validatePanelId(id) ? id : null;
  } catch (error) {
    console.error('Fehler beim Abrufen der Panel-ID:', error);
    return null;
  }
}

/**
 * Löscht gespeicherte Panel-ID (z.B. nach erfolgreichem Redirect)
 */
export function clearPanelId() {
  try {
    localStorage.removeItem(PANEL_ID_KEY);
    console.log('Panel-ID gelöscht');
  } catch (error) {
    console.error('Fehler beim Löschen der Panel-ID:', error);
  }
}

// ============================================================================
// REDIRECT FUNCTIONS
// ============================================================================

/**
 * Leitet User zurück zum Panel-Provider mit entsprechendem Status
 * 
 * @param {string} status - complete | screenout | quotasfull | quality | speeder | duplicate | geoip
 * @param {string} panelId - 32-stellige Panel-ID
 */
export function redirectToPanel(status, panelId) {
  console.log('=== REDIRECT TO PANEL ===');
  console.log('Status:', status);
  console.log('Panel-ID (raw):', panelId);
  console.log('Panel-ID Typ:', typeof panelId);
  console.log('Panel-ID Länge:', panelId ? panelId.length : 'null');
  
  if (!panelId) {
    console.error('❌ Redirect abgebrochen - Panel-ID ist null/undefined');
    return false;
  }
  
  if (!validatePanelId(panelId)) {
    console.error('❌ Redirect abgebrochen - Panel-ID Validierung fehlgeschlagen');
    return false;
  }

  const baseUrl = REDIRECT_URLS[status];
  
  if (!baseUrl) {
    console.error('❌ Redirect abgebrochen - unbekannter Status:', status);
    return false;
  }

  const redirectUrl = baseUrl + panelId;
  
  console.log('📍 Redirect-URL zusammengesetzt:', {
    status,
    baseUrl,
    panelId: panelId.substring(0, Math.min(8, panelId.length)) + '...',
    completeUrl: redirectUrl
  });

  // Redirect durchführen mit verbesserter Fehlerbehandlung
  try {
    console.log('🚀 REDIRECT WIRD JETZT DURCHGEFÜHRT ZU:', redirectUrl);
    
    // Methode 1: Standard Redirect
    window.location.href = redirectUrl;
    
    // Fallback nach 1 Sekunde falls Redirect blockiert wurde
    setTimeout(() => {
      // Prüfe ob wir noch auf der gleichen Seite sind
      if (window.location.href.includes('fairment-fragebogen')) {
        console.warn('Redirect scheint blockiert - versuche window.location.replace');
        window.location.replace(redirectUrl);
      }
    }, 1000);
    
    return true;
  } catch (error) {
    console.error('Redirect-Fehler:', error);
    
    // Fallback: Versuche alternative Methoden
    try {
      window.location.replace(redirectUrl);
      return true;
    } catch (fallbackError) {
      console.error('Fallback-Redirect auch fehlgeschlagen:', fallbackError);
      
      // Letzter Versuch: Link erstellen und klicken
      try {
        const link = document.createElement('a');
        link.href = redirectUrl;
        link.target = '_self';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return true;
      } catch (linkError) {
        console.error('Alle Redirect-Methoden fehlgeschlagen:', linkError);
        return false;
      }
    }
  }
}

/**
 * Prüft ob User aus Panel-System kommt
 */
export function isPanelUser() {
  const panelId = getPanelId();
  return panelId !== null && validatePanelId(panelId);
}

/**
 * Initialisiert Panel-Tracking beim App-Start
 * Erfasst und validiert Panel-ID aus URL
 * 
 * @returns {string|null} Panel-ID oder null
 */
export function initializePanelTracking() {
  console.log('=== PANEL TRACKING INITIALISIERUNG ==='); // DEBUG
  
  const panelId = capturePanelId();
  
  console.log('Erfasste Panel-ID:', panelId); // DEBUG
  console.log('Ist Panel-ID valide?', panelId ? validatePanelId(panelId) : 'keine ID'); // DEBUG
  
  if (panelId && validatePanelId(panelId)) {
    const stored = storePanelId(panelId);
    console.log('Panel-ID gespeichert?', stored); // DEBUG
    console.log('Panel-Tracking initialisiert für ID:', panelId.substring(0, 8) + '...');
    
    // Verifiziere localStorage
    console.log('Verifiziere localStorage Key "' + PANEL_ID_KEY + '":', localStorage.getItem(PANEL_ID_KEY)); // DEBUG
    
    return panelId;
  }
  
  // Prüfe ob ID in localStorage vorhanden (Page Refresh)
  const storedId = getPanelId();
  if (storedId) {
    console.log('Panel-ID aus localStorage wiederhergestellt:', storedId.substring(0, 8) + '...');
    return storedId;
  }
  
  console.log('Kein Panel-Tracking - direkter/organischer Zugriff');
  return null;
}

// ============================================================================
// EXPORT
// ============================================================================

export default {
  capturePanelId,
  validatePanelId,
  storePanelId,
  getPanelId,
  clearPanelId,
  redirectToPanel,
  isPanelUser,
  initializePanelTracking
};

